import React, { Fragment } from 'react';
import { observer } from 'mobx-react';
import { inject } from '../../utils';
import { action, observable, computed } from 'mobx';
import { withTheme } from 'styled-components';
import Translate, { translate } from '../Translate';
import ClickLayer from '../ClickLayer';
import Icon from '../Icon';
import { camelCase } from '@kurtosys/ksys-app-template/dist/utils/changeCase';
import InlineDisclaimers from '../InlineDisclaimers';
import { Popper } from '../../utils/Popper';
import { Modifier, OptionsGeneric } from '@popperjs/core';

import DropdownButton from './Components/DropdownButton';

import {
    DropdownDisclaimerWrapper,
    DropdownInnerWrapper,
    DropdownText,
    DropdownWrapper,
} from './StyledComponents';

import { DropdownItem } from '../../models/components/Dropdown/DropdownItem';
import { DropdownProps, DropdownThemeKeys } from './Models';
import DropdownMenu from './Components/DropdownMenu';
import { createPortal } from 'react-dom';
import { SuperscriptScope } from '../../stores';
import { DropdownEventHandler } from './DropdownEventHandler';

@inject('accessibilityStore', 'dataStore')
@observer
class Dropdown extends Popper<DropdownProps, {}, DropdownThemeKeys> {
    @observable.ref showDropdown: boolean = false;
    @observable.ref rawSelectedItemIndex: number = -1;
    dropdownEventHandler: DropdownEventHandler;

    DEFAULT_THEME_KEYS: DropdownThemeKeys = {
        button: 'DropdownButton',
        buttonIcon: 'DropdownChevron',
        buttonLabel: 'DropdownText',
        root: 'DropdownWrapper',
        list: 'DropdownList',
        listItem: 'DropdownListItem',
        listWrapper: 'DropdownMenu',
        wrapper: 'DropdownInnerWrapper',
        disclaimerWrapper: 'DropdownDisclaimerWrapper',
    };

    popperOptions: Partial<OptionsGeneric<Partial<Modifier<any, any>>>> = this
        .props?.popperOptions
        ? this.props.popperOptions
        : {
            placement: 'bottom-end',
            modifiers: [
                {
                    name: 'offset',
                    options: {
                        offset: [0, 1],
                    },
                },
                {
                    name: 'flip',
                    enabled: false,
                },
            ],
        };

    constructor(props) {
        super(props);
        this.dropdownEventHandler = new DropdownEventHandler({
            items: this.items,
            onItemSelect: this.handleOnClick,
            pageKeyTraverseSize: 3,
        });
    }

    @computed
    get popperEnabled(): boolean {
        return this.dropdownEventHandler.isOpen;
    }

    @computed
    get selectedItemIndex(): number {
        const { items, selectedItem } = this.props;
        let index = this.rawSelectedItemIndex;
        if (this.rawSelectedItemIndex < 0 && selectedItem !== undefined) {
            index = items.findIndex(item => item.id === selectedItem.id);
        }
        return index;
    }

    set selectedItemIndex(index: number) {
        this.rawSelectedItemIndex = index;
    }

    @computed
    get selectedItem() {
        return (
            this.props.selectedItem || this.props.items[this.selectedItemIndex]
        );
    }

    @computed
    get items(): Array<DropdownItem> {
        const { items } = this.props;
        return items;
    }

    @action
    setSelectedItem = (item: DropdownItem, index: number) => {
        this.selectedItemIndex = index;
        this.props.selectItemFn(item);
        this.dropdownEventHandler.closeDropdown();
    };

    @action
    handleOnClick = (
        event: React.SyntheticEvent<HTMLElement>,
        item: DropdownItem,
        index: number
    ) => {
        event.stopPropagation();
        // Dropdown items may contain a component, in which case selecting an item should click the nested component
        if (item.component) {
            this.dropdownEventHandler.clickNestedComponent(index);
        } else {
            this.setSelectedItem(item, index);
        }
    };

    @action
    setDropdownMenuElement = (element: HTMLElement) => {
        this.setPopupNodeRef(element);
        // Show the visually hidden Dropdown after Popper initialization, this ensures correct positioning
        if (element) {
            this.showDropdown = true;
            return;
        }
        this.showDropdown = false;
    };

    @computed
    get buttonKeyboardProps() {
        return {
            onKeyDown: this.dropdownEventHandler.keyboardEvents.handleKeyboardEventsFor(
                'down'
            ),
        };
    }

    getItemLabel(item: DropdownItem): string {
        if (item) {
            const { itemLabelFn } = this.props;
            if (itemLabelFn) {
                return itemLabelFn(item);
            }
            return item.label;
        }
        return;
    }

    render() {
        const {
            props: {
                children,
                selectItemFn: selectItem,
                itemLabelFn,
                hideLabel = false,
                items,
                placeHolder,
                chevronIcon,
                chevronIconExpanded,
                accessibilityStore,
                dataStore,
                buttonAccessibilityKey,
                themeKeys,
                theme,
                superscriptScope = SuperscriptScope.module,
                ...props
            },
            selectedItemIndex,
            selectedItem,
            handleOnClick,
            setDropdownMenuElement,
            showDropdown,
            dropdownEventHandler,
        } = this;

        const {
            toggleOpen,
            keyboardEvents,
            childReferences,
            isOpen,
            isHeaderFocused,
        } = dropdownEventHandler;

        if (!dataStore || !dataStore.shadowRoot) {
            return null;
        }

        const selectedLabel = this.getItemLabel(selectedItem) || placeHolder;

        const translatedSelectedLabel =
            (translate &&
                translate(dataStore.stores.translation, selectedLabel)) ||
            selectedLabel;

        const dropdownElement = dataStore.shadowRoot.getElementById(
            'fundcenter-dropdown'
        );

        return (
            <DropdownWrapper {...props} {...this.getTheme('root')}>
                <DropdownInnerWrapper
                    ref={this.setAnchorNodeRef}
                    {...this.getTheme('wrapper')}
                >
                    <DropdownButton
                        title={accessibilityStore.buttonAccessibilityValue(
                            buttonAccessibilityKey || 'DropdownButton'
                        )}
                        onClick={toggleOpen}
                        dataSelected={isOpen}
                        dataMetricId={`moduleDropdown`}
                        isFocused={isHeaderFocused}
                        {...this.buttonKeyboardProps}
                        {...this.getTheme('button')}
                    >
                        {!hideLabel && (
                            <DropdownText {...this.getTheme('buttonLabel')}>
                                <Translate>
                                    {selectedLabel
                                        ? selectedLabel
                                        : this.props.placeHolder}
                                </Translate>
                            </DropdownText>
                        )}
                        <Icon
                            fileName={
                                isOpen
                                    ? chevronIconExpanded ||
                                    'DropdownChevronIconExpanded'
                                    : chevronIcon || 'DropdownChevronIcon'
                            }
                            aria-hidden={true}
                            {...this.getTheme('buttonIcon')}
                        />
                    </DropdownButton>
                    {children}
                    {isOpen && (
                        <Fragment>
                            <ClickLayer
                                data-metric-id={'moduleDropdown-clickLayer'}
                                onClick={toggleOpen}
                            />
                            {createPortal(
                                <DropdownMenu
                                    items={items}
                                    handleOnClick={handleOnClick}
                                    selectedItemIndex={selectedItemIndex}
                                    itemLabelFn={itemLabelFn}
                                    dropdownMenuRef={setDropdownMenuElement}
                                    isOpen={isOpen}
                                    keyboardEvents={keyboardEvents}
                                    showDropdown={showDropdown}
                                    childReferences={childReferences}
                                    themeKeys={themeKeys}
                                    theme={theme}
                                    superscriptScope={superscriptScope}
                                    dropdownEventHandler={dropdownEventHandler}
                                />,
                                dropdownElement
                            )}
                        </Fragment>
                    )}
                    <DropdownDisclaimerWrapper
                        {...this.getTheme('disclaimerWrapper')}
                    >
                        <InlineDisclaimers
                            conditionalDisclaimers={
                                selectedItem?.disclaimers
                            }
                            superscriptScope={superscriptScope}
                            metricContext={`dropDown-${camelCase(
                                selectedLabel
                            )}`}
                            forceSuperScripts={dataStore.isMobile}
                            accessibilityContext={{
                                key: `More information about the {label}`,
                                placeholders: {
                                    label: translatedSelectedLabel
                                }
                            }}
                        />
                    </DropdownDisclaimerWrapper>
                </DropdownInnerWrapper>
            </DropdownWrapper>
        );
    }
}

export default withTheme(Dropdown);



// WEBPACK FOOTER //
// ./src/Components/Dropdown/index.tsx